// src/components/PropertyCard.js
import React, { useState, useEffect } from 'react';
import {
  Maximize, CheckCircle, Bed, Bath, Droplets, Car,
  Building, Sun, Zap, Shield, Leaf, Ruler, Home, Layers, Box, Heart
} from 'lucide-react';
import { formatImageUrl } from '../services/api';
import SafeImage from './SafeImage';
import { isFavorite as checkIsFavorite, toggleFavorite, onFavoritesChanged } from '../utils/favorites';

const FEATURE_ICONS = {
  maximize: Maximize,
  'check-circle': CheckCircle,
  bed: Bed,
  bath: Bath,
  droplets: Droplets,
  car: Car,
  building: Building,
  sun: Sun,
  zap: Zap,
  shield: Shield,
  tree: Leaf,
  leaf: Leaf,
  ruler: Ruler,
  home: Home,
  layers: Layers,
  box: Box
};

const getBadge = (property) => {
  if (property.badgeType && property.badgeText) {
    return { text: property.badgeText, type: property.badgeType };
  }
  if (property.featured) return { text: 'Destacado', type: 'destacado' };
  if (property.category === 'terreno') return { text: 'Terreno', type: 'terreno' };
  if (property.type === 'alquiler') return { text: 'Alquiler', type: 'alquiler' };
  if (property.category === 'departamento') return { text: 'Departamento', type: 'depto' };
  return { text: property.type === 'venta' ? 'Venta' : (property.status || 'Disponible'), type: 'default' };
};

const BADGE_STYLES = {
  destacado: 'bg-red-500 text-white',
  terreno: 'bg-emerald-500 text-white',
  alquiler: 'bg-purple-500 text-white',
  depto: 'bg-blue-500 text-white',
  default: 'bg-primary text-white'
};

const PropertyCard = ({ property, onViewDetails }) => {
  const formattedImage = formatImageUrl(property.image || (property.images && property.images[0]));
  const features = property.features || [];
  const badge = getBadge(property);
  const [favorite, setFavorite] = useState(false);

  useEffect(() => {
    setFavorite(checkIsFavorite(property.id));
    return onFavoritesChanged(() => setFavorite(checkIsFavorite(property.id)));
  }, [property.id]);

  return (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
      favorite ? 'ring-2 ring-red-400' : ''
    }`}>
      <div className="relative h-64 overflow-hidden">
        <SafeImage
          src={formattedImage}
          alt={property.title}
          className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
        />

        {/* Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold shadow-md ${BADGE_STYLES[badge.type] || BADGE_STYLES.default}`}>
            {badge.text}
          </span>
        </div>

        {/* Botón de me gusta */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setFavorite(toggleFavorite(property.id));
          }}
          className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white backdrop-blur-sm rounded-full shadow-md transition-colors"
          aria-label={favorite ? 'Quitar de me gusta' : 'Agregar a me gusta'}
        >
          <Heart className={`w-5 h-5 transition-colors ${favorite ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
        </button>

        {/* Precio */}
        <div className="absolute bottom-4 right-4">
          <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
            <span className="text-lg font-bold text-gray-900">
              {property.price}
            </span>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-1">
          {property.title}
        </h3>
        
        <p className="text-gray-600 mb-4 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          {property.location}
        </p>
        
        {/* Características principales */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {features.slice(0, 4).map((feature, index) => {
            const Icon = FEATURE_ICONS[feature.icon] || CheckCircle;
            return (
              <div key={index} className="flex items-center">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                  <Icon className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-800">{feature.value || feature.text}</div>
                  <div className="text-xs text-gray-500">{feature.label || feature.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Botón */}
        <button
          onClick={() => onViewDetails(property)}
          className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
        >
          <span>Ver Detalles</span>
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PropertyCard;